import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/electron/renderer'
import { RemindersDataContext } from './reminders-context'
import { useReminders } from './hooks/use-reminders'
import { useReminderLingerRelease } from './hooks/reminder-linger'
import { useSendClearedTodos } from './hooks/use-send-cleared-todos'
import { subscribeMeetingFollowUpsSync } from './hooks/meeting-follow-ups-sync'

/**
 * Runs `useReminders` once for the whole sidepanel. The reminders filter toggle,
 * overdue badge, home section, and drawer all read from this context, so the
 * O(n) reminder mapping/filtering (and the query observer) run a single time
 * instead of once per consumer. `useReminders` returns a stable object, so this
 * provider's re-renders don't cascade to the stable children.
 *
 * Kept as this file's only export so it stays a Fast Refresh boundary — the
 * context it provides lives in `reminders-context.ts` (see the note there).
 */
export function RemindersDataProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const reminders = useReminders()
  // Recap unmounts on tab changes, but its cached reminders still need invalidation.
  useEffect(() => subscribeMeetingFollowUpsSync(queryClient, Sentry.captureException), [queryClient])
  // One place for the whole panel retires just-completed reminders, once the
  // user leaves the lists holding them open.
  useReminderLingerRelease(useSendClearedTodos())

  return <RemindersDataContext.Provider value={reminders}>{children}</RemindersDataContext.Provider>
}
